'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { optimizelyClient } from './optimizelyClient';
import { OptimizelyDecision } from '@optimizely/react-sdk';

export type UseOptimizelyVariationOptions = {
  /**
   * If false, no decisions will be made until true.
   * @deprecated This only exists for backwards compatibility. Please just don't call this if you don't want it decisioned yet...
   */
  shouldDecide?: boolean,
  /**
   * If true, the hook will wait for the optimizely client to have been updated with user info before decisioning.
   */
  requireUserInfo?: boolean,
  /**
   * An optional callback that can be used to track feature flag variation decisions.
   */
  analyticsCallback?: (flagName: string, decision: OptimizelyDecision | null) => void
};

/**
 * @returns true if the optimizely's user ID is not the anonymous VUID.
 */
const userIdIsSet = () => optimizelyClient.user.id !== optimizelyClient.getVuid();

export const useOptimizelyVariation = (
  /**
   * The flag name/key to evaluate and return the variation for.
   */
  flagKey: string,
  /**
   * Optional configuration for the hook. By default, the hook will decide immediately and will not wait for user info to be set on the optimizely client.
   */
  options: UseOptimizelyVariationOptions | undefined = undefined
) => {
  const defaults = {
    shouldDecide: true,
    requireUserInfo: false,
  };
  const combinedOptions = { ...defaults, ...options };
  const { shouldDecide, requireUserInfo, analyticsCallback } = combinedOptions;

  const [variation, setVariation] = useState<string | null>(null);
  const [decision, setDecision] = useState<OptimizelyDecision | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [optimizelySdkLoaded, setOptimizelySdkLoaded] = useState(false);
  const [optimizelyUserSet, setOptimizelyUserSet] = useState(false);
  const hasDecided = useRef(false);

  // Reset decision guard when inputs change so a fresh decision is made
  useEffect(() => {
    hasDecided.current = false;
  }, [flagKey, shouldDecide]);

  useEffect(() => { // register handlers for onReady and onUserUpdate
    const disposeFunction = optimizelyClient.onUserUpdate(() => {
      if (userIdIsSet()) {
        setOptimizelyUserSet(true);
      }
    });
    optimizelyClient.onReady().then(() => {
      // check if user info is already set on the optimizely client,
      // as onReady can be called after user info is set
      setOptimizelyUserSet(userIdIsSet());

      setOptimizelySdkLoaded(true);
    });
    return disposeFunction;
  }, []);

  const getVariationByRule = useCallback(async () => {
    if (hasDecided.current) return;

    if (!shouldDecide) {
      setIsLoading(false);
      return;
    }

    hasDecided.current = true;
    const variationResponse = optimizelyClient.decide(flagKey, []);
    setVariation(variationResponse.variationKey);
    setDecision(variationResponse);
    setIsLoading(false);

    if (analyticsCallback && variationResponse) {
      analyticsCallback(flagKey, variationResponse);
    }
  }, [flagKey, shouldDecide]);

  useEffect(() => {
    if (!optimizelySdkLoaded) {
      return;
    }
    if (!requireUserInfo || optimizelyUserSet) {
      getVariationByRule();
    }
  }, [optimizelySdkLoaded, optimizelyUserSet, getVariationByRule]);

  return {
    variation,
    decision,
    isLoading,
  };
};
